// Client-side velocity tracker for the open-order cards. The relay keeps no
// per-order time series, so we sample queue depth + filled count once per
// poll cycle and diff against in-memory history to surface 5m / 30m / 1h
// movement. History lives only as long as the page is open and reports null
// for a window until enough history has accumulated to cover it.

interface Sample {
  t: number;
  queue: number | null;
  filled: number;
}

const HISTORY = new Map<string, Sample[]>();
const LAST_APPEND_AT = new Map<string, number>();
const MAX_AGE_MS = 65 * 60 * 1000;
// Collapse samples taken closer together than this (rapid re-renders) into
// one, so the buffer tracks the ~5s poll cadence rather than render churn.
const MIN_GAP_MS = 2000;

/** Record the current queue depth + filled count for an order. Call once
 *  per poll cycle for every visible order. Returns true when a new ~2s history
 *  point was appended, false when the call collapsed into the last point
 *  (< MIN_GAP_MS since the previous append) — callers that share this cadence
 *  (e.g. the spike tracker) can gate off the append rather than the raw call. */
export function recordOrderSample(
  key: string,
  queue: number | null,
  filled: number,
): boolean {
  const now = Date.now();
  let arr = HISTORY.get(key);
  if (!arr) {
    arr = [];
    HISTORY.set(key, arr);
  }
  const last = arr[arr.length - 1];
  const lastAppendAt = LAST_APPEND_AT.get(key);
  let appended: boolean;
  if (last && lastAppendAt != null && now - lastAppendAt < MIN_GAP_MS) {
    last.t = now;
    last.queue = queue;
    last.filled = filled;
    appended = false;
  } else {
    arr.push({ t: now, queue, filled });
    LAST_APPEND_AT.set(key, now);
    appended = true;
  }
  const cutoff = now - MAX_AGE_MS;
  while (arr.length > 1 && arr[0].t < cutoff) arr.shift();
  return appended;
}

export interface VelocityWindow {
  /** Change in queue depth over the window. Negative = advancing toward the
   *  front of the book. Null until history reaches back that far. */
  queueDelta: number | null;
  /** Contracts filled over the window. Null until history reaches back. */
  filledDelta: number | null;
}

const EMPTY: VelocityWindow = { queueDelta: null, filledDelta: null };

function deltaOver(arr: Sample[], windowMs: number): VelocityWindow {
  const cur = arr[arr.length - 1];
  const target = Date.now() - windowMs;
  let base: Sample | null = null;
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    if (arr[i].t <= target) {
      base = arr[i];
      break;
    }
  }
  if (!base) return EMPTY;
  return {
    queueDelta:
      cur.queue != null && base.queue != null ? cur.queue - base.queue : null,
    filledDelta: cur.filled - base.filled,
  };
}

export interface OrderVelocity {
  m1: VelocityWindow;
  m5: VelocityWindow;
  m30: VelocityWindow;
  h1: VelocityWindow;
}

/** Velocity for an order across the 1m / 5m / 30m / 1h windows. */
export function orderVelocity(key: string): OrderVelocity {
  const arr = HISTORY.get(key);
  if (!arr || arr.length < 2) return { m1: EMPTY, m5: EMPTY, m30: EMPTY, h1: EMPTY };
  return {
    m1: deltaOver(arr, 60_000),
    m5: deltaOver(arr, 5 * 60_000),
    m30: deltaOver(arr, 30 * 60_000),
    h1: deltaOver(arr, 60 * 60_000),
  };
}

/** Drop history for orders no longer on the page. */
export function pruneOrderVelocity(activeKeys: Set<string>): void {
  for (const key of HISTORY.keys()) {
    if (!activeKeys.has(key)) {
      HISTORY.delete(key);
      LAST_APPEND_AT.delete(key);
    }
  }
}
